import React, { useState, useEffect } from "react";
import "./LiveTimer.css";
import { EventFetchSlides } from "../../services/event/attendee-event-slides";

interface LiveTimerProps {
  isLive: boolean;
  eventId?: number;
}

const LiveTimer: React.FC<LiveTimerProps> = ({ isLive, eventId }) => {
  const [time, setTime] = useState<number>(0);
  const [lastStoppedTime, setLastStoppedTime] = useState<number>(0);
  const [webinarStartTime, setWebinarStartTime] = useState<Date | null>(null);
  const [serverCurrentTime, setServerCurrentTime] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [clientServerTimeDiff, setClientServerTimeDiff] = useState<number>(0);

  // Fetch webinar start time from API when going live
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    
    const fetchWebinarData = async () => {
      if (isLive && eventId && !isInitialized) {
        try {
          const response = await EventFetchSlides(eventId);
          // Check if the API response includes webinar timing data
          if (response.currentWebinarStartTime) {
            const startTime = new Date(response.currentWebinarStartTime);
            if (!isNaN(startTime.getTime())) {
              setWebinarStartTime(startTime);
              // Store server current time to calculate initial elapsed time
              if (response.serverCurrentTime) {
                const serverTime = new Date(response.serverCurrentTime);
                setServerCurrentTime(serverTime);
                // Calculate the initial elapsed time based purely on server timestamps
                const initialElapsed = Math.floor((serverTime.getTime() - startTime.getTime()) / 1000);
                setClientServerTimeDiff(Math.max(0, initialElapsed));
              }
              setIsInitialized(true);
              // Clear polling interval once we have the start time
              if (pollInterval) {
                clearInterval(pollInterval);
              }
            } else {
              throw new Error('Invalid webinar start time from API');
            }
          } else {
            console.log('No webinar start time found in API response');
          }
        } catch (error) {
          console.error('Error fetching webinar data:', error);
        }
      } else if (!isLive) {
        // When not live, reset initialization state
        setIsInitialized(false);
        setWebinarStartTime(null);
        setServerCurrentTime(null);
        setClientServerTimeDiff(0);
        if (pollInterval) {
          clearInterval(pollInterval);
        }
      }
    };
    // Initial fetch
    fetchWebinarData();
    // If we're live but don't have start time yet, poll every 3 seconds
    if (isLive && eventId && !isInitialized) {
      pollInterval = setInterval(fetchWebinarData, 3000);
    }
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isLive, eventId, isInitialized]);

  // Timer logic
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    let startTimestamp: number | null = null;

    if (isLive && webinarStartTime && isInitialized) {
      // Record when we start the timer (using performance.now() for precision)
      startTimestamp = performance.now();
      // Calculate elapsed time without relying on client clock
      const calculateElapsedTime = () => {
        if (startTimestamp !== null) {
          // Calculate seconds elapsed since timer started using high-precision timer
          const secondsSinceTimerStart = Math.floor((performance.now() - startTimestamp) / 1000);
          // Add to the initial elapsed time we calculated from server
          const totalElapsed = clientServerTimeDiff + secondsSinceTimerStart;
          return Math.max(0, totalElapsed);
        }
        return clientServerTimeDiff;
      };

      // Set initial time
      setTime(calculateElapsedTime());

      interval = setInterval(() => {
        setTime(calculateElapsedTime());
      }, 1000);
    } else if (!isLive) {
      // Store the current time when stopping
      setLastStoppedTime(time);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLive, webinarStartTime, isInitialized, clientServerTimeDiff]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const pad = (num: number): string => num.toString().padStart(2, "0");

    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  };

  return (
    <div className={`live-timer ${isLive ? 'active' : ''}`}>
      <span className="timer-label">Live Duration: </span>
      <span className="timer-value" data-testid="timer-value">
        {isLive && !isInitialized ? 'Loading...' : (isLive ? formatTime(time) : formatTime(lastStoppedTime))}
      </span>
    </div>
  );
};

export default LiveTimer;